import { useRouter } from 'next/router';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { UserRole } from '~/api/account/accountApiTypes';
import { pagesConfigStore } from '~/atomic/atom/links/pagesConfigStore';
import { AntiPirateCheckLink, MyBlogLink } from '~/atomic/atom/links/staticLinks';
import { PaperStyle } from '~/atomic/atom/PaperStyle';
import { LandingTextStyle } from '~/atomic/Typography';
import { openModal } from '~/feature/authorization/authorizationModal.slice';
import { isLoggedInSelector } from '~/feature/user/isLoggedInSelector';
import { isLoggedUserCommercialAuthorSelector, userSelector } from '~/feature/user/user.selector';
import { lessThan } from '~/lib/mediaQuery';
import { useAppDispatch, useAppSelector } from '~/store';

const PLAYER_ID = 'youtube-player-1';

export const AntiPirateLandingPage = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const { user } = useAppSelector(userSelector);
  const isLoggedUserCommercialAuthor = useAppSelector(isLoggedUserCommercialAuthorSelector);

  const playerRef = useRef(null);
  const [isShowPlayer, setIsShowPlayer] = useState(false);

  const onPlayerReady = (event) => {
    setIsShowPlayer(true);
    event.target.playVideo();
  };

  const onClickPlayVideoHandler = () => {
    const scriptTag = document.createElement('script');
    scriptTag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(scriptTag, firstScriptTag);

    (window as any).onYouTubeIframeAPIReady = () => {
      playerRef.current = new (window as any).YT.Player(PLAYER_ID, {
        height: '390',
        width: '640',
        frameBorder: 0,
        allowFullscreen: true,
        videoId: 'GAznAWeo9P4',
        events: { onReady: onPlayerReady },
      });
    };
  };

  const onClickRegisterHandler = () => {
    if (isLoggedIn) {
      if (isLoggedUserCommercialAuthor) {
        router.push(pagesConfigStore.books.url);
      } else if (user.role === UserRole.author) {
        router.push('/author/registration');
      }
    } else {
      dispatch(openModal('login'));
    }
  };

  return (
    <SCAntiPirateLandingPage>
      <SCHeroBlock>
        <SCHeroBlockContent>
          <SCHeroBlockHeaders>
            <SCHeroTitleH1>
              Уникальная защита произведений от книжных пиратов
            </SCHeroTitleH1>
            <SCHerotitleSignature>
              Продавая на Букривер, вы получаете возможность защитить
              свои книги и уменьшить количество нелегально распространяемых копий
            </SCHerotitleSignature>
            <SCHeroBlockRegisterButton
              onClick={onClickRegisterHandler}
            >
              {isLoggedUserCommercialAuthor
                ? 'Начать защищать книги'
                : 'Зарегистрироваться как автор'}
            </SCHeroBlockRegisterButton>
          </SCHeroBlockHeaders>
        </SCHeroBlockContent>
      </SCHeroBlock>
      <SCLosses>
        <SCLossesContent>
          <SCLossesStatisticsWrapper>
            <SCLossesStatisticsDescription>
              Сумма недополученных гонораров правообладателями за 2019 год
            </SCLossesStatisticsDescription>
            <SCLossesStatistics>
              <SCLossesStatisticsSum>
                <SCLossesStatisticsCount>
                  55
                </SCLossesStatisticsCount>
                <SCLossesStatisticsSumText>
                  млрд руб.
                </SCLossesStatisticsSumText>
              </SCLossesStatisticsSum>
              <SCLossesStatisticsBooks>
                <SCLossesStatisticsBooksCount>
                  30%
                </SCLossesStatisticsBooksCount>
                <SCLossesStatisticsBooksText>
                  распространение книг читателями
                </SCLossesStatisticsBooksText>
              </SCLossesStatisticsBooks>
              <SCLossesStatisticsPiracy>
                <SCLossesStatisticsPiracyCount>
                  70%
                </SCLossesStatisticsPiracyCount>
                <SCLossesStatisticsPiracyText>
                  пираты
                </SCLossesStatisticsPiracyText>
              </SCLossesStatisticsPiracy>
            </SCLossesStatistics>
          </SCLossesStatisticsWrapper>
          <SCLossesInfo>
            <SCLossesTitle>
              Если вы автор, то:
            </SCLossesTitle>
            <SCLossesList>
              <SCLossesItem>
                —  бывали в ситуации, когда ваша платная книга появлялась
                в бесплатном доступе. Иногда даже раньше, чем вы ее дописали.
              </SCLossesItem>
              <SCLossesItem>
                —  знаете, как больно копирование пиратами бьет по доходам.
                Ведь после него продажи падают моментально и в разы.
              </SCLossesItem>
            </SCLossesList>
            <SCLossesSecondList>
              <SCLossesSecondItem>
                Не удивляйтесь. Не только пираты, но и ваши читатели выстраивают гигантскую
                цепочку из бесплатных копий ваших произведений, пересылая их
                друзьям и размещая на нелегальных площадках.
              </SCLossesSecondItem>
              <SCLossesSecondItem>
                Большинство читателей делают это из желания порекомендовать
                понравившуюся главу или книгу. Но никто из них не понимает,
                как сильно это уменьшает ваш доход.
              </SCLossesSecondItem>
            </SCLossesSecondList>
          </SCLossesInfo>
        </SCLossesContent>
      </SCLosses>
      <SCHowWork>
        <SCHowWorkContent>
          <SCHowWorkInfo>
            <SCHowWorkTitle>
              Принцип работы защиты
            </SCHowWorkTitle>
            <SCHowWorkList>
              <SCHowWorkItem>
                <SCHowWorkNumberWrapper>
                  <SCHowWorkNumber>
                    1
                  </SCHowWorkNumber>
                </SCHowWorkNumberWrapper>
                Автор особенным образом маркирует платные тексты перед публикацией
              </SCHowWorkItem>
              <SCHowWorkItem>
                <SCHowWorkNumberWrapper>
                  <SCHowWorkNumber>
                    2
                  </SCHowWorkNumber>
                </SCHowWorkNumberWrapper>
                Каждый читатель, приобретающий произведение на Bookriver,
                получает его уникальную копию
              </SCHowWorkItem>
              <SCHowWorkItem>
                <SCHowWorkNumberWrapper>
                  <SCHowWorkNumber>
                    3
                  </SCHowWorkNumber>
                </SCHowWorkNumberWrapper>
                Если такая копия оказывается в публичном доступе, то маркировка
                позволяет определить, какому читателю эта копия
                принадлежит, т.е. кто нарушил авторские права
              </SCHowWorkItem>
              <SCHowWorkItem>
                <SCHowWorkNumberWrapper>
                  <SCHowWorkNumber>
                    4
                  </SCHowWorkNumber>
                </SCHowWorkNumberWrapper>
                Такой читатель блокируется на сайте Bookriver и теряет доступ
                ко всем купленным произведениям и бонусам
              </SCHowWorkItem>
              <SCHowWorkItem>
                <SCHowWorkNumberWrapper>
                  <SCHowWorkNumber>
                    5
                  </SCHowWorkNumber>
                </SCHowWorkNumberWrapper>
                Чем больше глав разметит автор, тем выше вероятность того, что защиту не обойдут
              </SCHowWorkItem>
            </SCHowWorkList>
          </SCHowWorkInfo>
          <SCHowWorkImageWrapper>
            <AntiPirateCheckLink>
              <a
                target="_blank"
                rel="noopener noreferrer"
              >
                <SCHowWorkImage
                  src="/image/anti-pirate/how-work.jpg"
                  alt="изображение работы защиты"
                />
              </a>
            </AntiPirateCheckLink>
          </SCHowWorkImageWrapper>
        </SCHowWorkContent>
      </SCHowWork>
      <SCHowHelp>
        <SCContent>
          <SCHowHelpTitle>
            Как сервис помогает бороться с нелегальным распространением книг
          </SCHowHelpTitle>
          <SCHowHelpList>
            <SCHowHelpItem>
              Заблокированные читатели будут вынуждены оплачивать
              новые главы книги, чтобы продолжить читать их и размещать
              в публичном доступе нелегальным образом.
              Авторы смогут больше заработать, пока книга пишется,
              даже если их произведение постоянно скачивают
            </SCHowHelpItem>
            <SCHowHelpItem>
              Идентифицированный читатель, допустивший появление копии в публичном доступе,
              может быть привлечен к ответственности за нарушение авторских прав и
              причинение материального вреда по статье 1301 ГК РФ. Площадка готова
              помогать идентифицировать таких читателей и получать от них компенсации
              в судебном порядке.
            </SCHowHelpItem>
          </SCHowHelpList>
        </SCContent>
      </SCHowHelp>
      <SCRegistration>
        <SCContent>
          <SCWave src="/icon/wave-blue.svg" alt="иконка волны" />
          <SCRegistrationTitle>
            Разместите и защитите свои книги на Bookriver прямо сейчас
          </SCRegistrationTitle>
          <SCRegisterButton
            onClick={onClickRegisterHandler}
          >
            {isLoggedUserCommercialAuthor
              ? 'Начать защищать книги'
              : 'Зарегистрироваться как автор'}
          </SCRegisterButton>
        </SCContent>
      </SCRegistration>
      <SCHowUse>
        <SCContent>
          <SCPlayerContainer data-is-show-player={String(isShowPlayer)}>
            <SCVideoInfoContainer>
              <SCPlayerHeader>
                Как маркировать главы
              </SCPlayerHeader>
              <SCPlayButton onClick={onClickPlayVideoHandler} />
              <SCVideoMeta>
                время просмотра
              </SCVideoMeta>
              <SCVideoTime>
                2 мин 30 сек
              </SCVideoTime>
            </SCVideoInfoContainer>
            <SCYTPlayerContainer>
              <div id={PLAYER_ID} />
            </SCYTPlayerContainer>
          </SCPlayerContainer>
        </SCContent>
      </SCHowUse>
      <SCShareAndCheck>
        <SCShareAndCheckContent>
          <SCCheck>
            <SCCheckText>
              Проверяйте свои книги, найденные в публичном доступе.
              Выявляйте и наказывайте владельцев таких копий
            </SCCheckText>
            <AntiPirateCheckLink>
              <SCCheckButton as="a">
                Проверить копию книги
              </SCCheckButton>
            </AntiPirateCheckLink>
          </SCCheck>
          <SCShare>
            <SCCheckText>
              Рассказывайте своим читателям увлекательные истории об
              охоте на пиратов, сдерживая их тем самым, от
              нарушения ваших авторских прав
            </SCCheckText>
            <MyBlogLink>
              <SCShareButton as="a">
                Напишите в блог
              </SCShareButton>
            </MyBlogLink>
          </SCShare>
        </SCShareAndCheckContent>
        <SCCurve />
      </SCShareAndCheck>
    </SCAntiPirateLandingPage>
  );
};

const SCAntiPirateLandingPage = styled.div``;

const SCButton = styled.button`
  background: var(--primary-color);
  border-radius: 40px;
  font-family: var(--main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 24px;
  text-align: center;
  color: var(--white-color);
  padding: 20px 36px;
  border: unset;
  cursor: pointer;
  transition: background 0.3s ease; 
  
  &:focus {
    outline: unset;
  }
  
  &:hover {
    background: var(--primary-hover-color);
  }
  
  ${lessThan('md')} {
    font-size: 18px;
    line-height: 24px;
    padding: 16px 26px;
  }
`;

const SCWhiteButton = styled(SCButton)`
  background-color: var(--white-color);
  color: var(--primary-color);
  transition:
  color 0.2s ease-in-out, 
  background-color 0.2s ease-in-out
  ;
  
  &:hover {
    background-color: transparent;
    color: var(--white-color);
    border: 2px solid var(--white-color);
    padding: 18px 34px;
  }
  
  ${lessThan('sm')} {
    font-size: 20px;
    line-height: 24px;
  }
  
  ${lessThan('xs')} {
    font-size: 18px;
    line-height: 22px;
    padding: 12px 18px;
    margin-top: 40px; 
  }

`;

const SCRegisterButton = styled(SCButton)`
  margin-top: 56px;
  padding: 20px 83px;

  
  ${lessThan('md')} {
    margin-top: 40px;
    padding: 16px 60px;
  }
    
  ${lessThan('sm')} {
    padding: 16px 40px;
  }
`;

// const SCSmallText = styled.span`
//   font-family: var(--main-font);
//   font-style: normal;
//   font-weight: normal;
//   font-size: 18px;
//   line-height: 26px;
//   color: var(--gray-color);

//   ${lessThan('md')} {
//     text-align: center;
//     font-size: 15px;
//     line-height: 20px;
//   }
// `;

const SCHeroTitleH1 = styled.h1`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 48px;
  line-height: 48px;
  margin-bottom: 0;

  color: var(--white-color);

  ${lessThan('md')} {
    font-size: 40px;
    line-height: 40px;
  }

  ${lessThan('sm')} {
    font-size: 30px;
    line-height: 36px;
    text-align: center;
  }

  ${lessThan('xs')} {
    font-size: 24px;
    line-height: 30px;
    text-align: center;
  }
`;

const SCHeroBlockHeaders = styled.div`
  max-width: 620px;

  ${lessThan('sm')} {
    max-width: none;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const SCHeroBlockContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: flex-start;
  padding: 119px 256px 107px;

  ${lessThan('xl')} {
    padding: 50px 100px;
  }

  ${lessThan('md')} {
    padding: 37px 74px;
  }

  ${lessThan('sm')} {
    padding: 30px 15px;
  }
`;

const SCHeroBlock = styled.section`
  position: relative;
  width: 100%;
  max-width: 1400px;
  background-image: url('/image/anti-pirate/main.jpg');
  background-size: cover;
  background-position: center;
  margin-left: auto;
  margin-right: auto;
  height: 614px;

  ${lessThan('md')} {
    padding-top: 44.05%;
    height: unset;
    min-height: 440px;
  }

  ${lessThan('sm')} {
    min-height: 400px;
  }

  ${lessThan('xs')} {
    min-height: 440px;
  }
`;

const SCHerotitleSignature = styled.h2`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 28px;
  color: #fff;
  padding-top: 48px;
  margin-bottom: 0;
  
  ${lessThan('sm')} {
    text-align: center;
  }
 
  ${lessThan('xs')} {
    font-size: 18px;
    line-height: 24px;
    padding-top: 40px;
    text-align: center;
  }
  
  ${lessThan('xs')} {
    font-size: 16px;
    line-height: 22px;
    padding-top: 30px;
  }
`;

const SCHeroBlockRegisterButton = styled(SCWhiteButton)`
  margin-top: 49px;
`;

const SCContent = styled.div`
  display: flex;
  flex-direction: column;
  max-width: var(--container-width);
  align-items: center;
  margin-left: auto;
  margin-right: auto;

  ${lessThan('md')} {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const SCLosses = styled.div`
  margin-top: 95px;
  padding-bottom: 60px;

  ${lessThan('md')} {
    margin-top: 70px;
    padding-bottom: 50px;
  }

  ${lessThan('sm')} {
    margin-top: 40px;
    padding-bottom: 30px;
  }
`;

const SCLossesStatisticsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 420px;
  width: 100%;

  ${lessThan('md')} {
    margin-top: 30px;
  }
`;

const SCLossesInfo = styled.div`
  max-width: 586px;
  width: 100%;
  padding-left: 20px;
  
  ${lessThan('md')} {
    max-width: none;
    padding-left: 0;
  }
`;

const SCLossesTitle = styled.h2`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 36px;
  color: var(--black-color);
  margin-bottom: 0;
`;

const SCLossesList = styled.ul`
  display: flex;
  flex-direction: column;
  margin-top: 39px;

  > *:not(:first-child) {
    margin-top: 19px;
  }
`;

const SCLossesSecondList = styled.ul`
  display: flex;
  flex-direction: column;
  margin-top: 51px;

  > *:not(:first-child) {
    margin-top: 17px;
  }
`;

const SCLossesItem = styled.li`
  ${LandingTextStyle};
  list-style-type: none;
  color: var(--black-color);
`;

const SCLossesSecondItem = styled.li`
  ${LandingTextStyle};
  list-style-type: none;
  color: var(--gray-color);
`;

const SCLossesStatisticsDescription = styled.span`
  ${LandingTextStyle};
  color: var(--gray-color);
  margin-left: 100px;

  ${lessThan('sm')} {
    margin-left: 30px;
  }
`;

const SCLossesContent = styled(SCContent)`
  flex-direction: row;
  justify-content: space-between;
  padding-right: 16px;
  padding-left: 16px;

  ${lessThan('md')} {
    flex-direction: column-reverse;
  }
`;

const SCLossesStatistics = styled.div`
  width: 420px;
  height: 420px;
  border: 2px solid var(--black-color);
  box-sizing: border-box;
  border-radius: 50%;
  position: relative;
  margin-top: 18px;

  ${lessThan('sm')} {
    width: 300px;
    height: 300px;
  }
`;

const SCLossesStatisticsSum = styled.div`
  position: absolute;
  right: 111px;
  top: -4px;
  background: var(--bg-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${lessThan('sm')} {
    right: 60px;
    top: -7px;
  }
`;

const SCLossesStatisticsCount = styled.span`
  font-family: var(--landing-font);
  font-style: normal;
  font-weight: bold;
  font-size: 56px;
  line-height: 48px;
  color: var(--black-color);
  
  ${lessThan('sm')} {
    font-size: 40px;
    line-height: 38px;
  }
`;

const SCLossesStatisticsSumText = styled.span`
  font-family: var(--landing-font);
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 16px;
  color: var(--black-color);
  
  ${lessThan('sm')} {
    font-size: 16px;
    line-height: 14px;
  }
`;

const SCLossesStatisticsBooks = styled.div`
  position: absolute;
  top: 72px;
  left: 33px;
  background: var(--blue-color);
  display: flex;
  flex-direction: column;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  width: 132px;
  height: 132px;
  padding: 15px;
  
  ${lessThan('sm')} {
    width: 120px;
    height: 120px;
    padding: 10px;
    top: 35px;
    left: 25px;
  }
`;

const SCLossesStatisticsBooksCount = styled.span`
  font-family: var(--landing-font);
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 24px;
  color: var(--black-color);

  ${lessThan('sm')} {
    font-size: 24px;
    line-height: 24px;
  }
`;

const SCLossesStatisticsBooksText = styled.span`
  font-family: var(--landing-font);
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  line-height: 16px;
  color: var(--black-color);
  display: flex;
  text-transform: uppercase;
  text-align: center;
  padding-top: 6px;

  ${lessThan('sm')} {
    font-size: 9px;
    line-height: 12px;
  }
`;

const SCLossesStatisticsPiracy = styled.div`
  position: absolute;
  top: 135px;
  right: 28px;
  background: var(--yellow-color);
  display: flex;
  flex-direction: column;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  width: 248px;
  height: 248px;

  ${lessThan('sm')} {
    width: 150px;
    height: 150px;
    top: 120px;
  }
`;

const SCLossesStatisticsPiracyCount = styled.span`
  font-family: var(--landing-font);
  font-style: normal;
  font-weight: bold;
  font-size: 48px;
  line-height: 42px;
  color: var(--black-color);
`;

const SCLossesStatisticsPiracyText = styled.span`
  font-family: var(--landing-font);
  font-style: normal;
  font-weight: bold;
  font-size: 12px;
  line-height: 16px;
  color: var(--black-color);
  text-transform: uppercase;
  text-align: center;
  display: flex;
  padding-top: 8px;
`;

const SCHowWork = styled.div`
  margin-top: 95px;

  ${lessThan('md')} {
    margin-top: 70px;
  }

  ${lessThan('sm')} {
    margin-top: 50px;
  }
`;

const SCHowWorkInfo = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 560px;
  width: 100%;
  padding-right: 24px;

  ${lessThan('md')} {
    padding-right: 0;
    max-width: none;
    padding-bottom: 30px;
  }
`;

const SCHowWorkTitle = styled.h2`
  display: flex;
  flex-direction: column;
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 36px;
  text-align: center;
  margin-bottom: 0;
`;

const SCHowWorkImageWrapper = styled.div`
  border-radius: 16px;
  overflow: hidden;
  max-width: 600px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
`;

const SCHowWorkImage = styled.img`
  width: 100%;
`;

const SCHowWorkContent = styled(SCContent)`
  flex-direction: row;
  justify-content: space-between;
  padding-left: 16px;
  padding-right: 16px;

  ${lessThan('md')} {
    flex-direction: column;
  }
`;

const SCHowWorkList = styled.ul`
  display: flex;
  flex-direction: column;
  margin-top: 45px;

  > *:not(:first-child) {
    margin-top: 32px;
  }

  ${lessThan('sm')} {
    margin-top: 30px;
  }
`;

const SCHowWorkItem = styled.li`
  ${LandingTextStyle};
  display: flex;
  align-items: center;
  color: var(--black-color);
`;

const SCHowWorkNumberWrapper = styled.div`
  display: flex;
  padding-right: 13px;
  opacity: 0.3;
  justify-content: flex-end;
  max-width: 60px;
  width: 100%;
`;

const SCHowWorkNumber = styled.span`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 80px;
  line-height: 60px;
  color: var(--gray3-color);
`;

const SCHowHelp = styled.div`
  margin-top: 160px;

  ${lessThan('xl')} {
    margin-top: 120px;
  }

  ${lessThan('md')} {
    margin-top: 100px;
  }
`;

const SCHowHelpTitle = styled.h2`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 36px;
  text-align: center;
  margin-bottom: 0;
  color: var(--black-color);
`;

const SCHowHelpList = styled.ul`
  display: flex;
  margin-top: 64px;
  flex-wrap: wrap;
  align-items: flex-start;

  > *:not(:last-child) {
    margin-right: 24px;
  }

  ${lessThan('md')} {
    margin-top: 40px;

    > *:not(:last-child) {
      margin-right: 0;
      margin-bottom: 24px;
    }
  }
`;

const SCHowHelpItem = styled.li`
  ${LandingTextStyle};
  ${PaperStyle};
  display: flex;
  padding: 30px;
  color: var(--black-color);
  max-width: 486px;
  width: 100%;

  ${lessThan('md')} {
    max-width: none;
  }

  ${lessThan('sm')} {
    padding: 20px;
  }
`;

const SCWave = styled.img`
`;

const SCRegistration = styled.div`
  margin-top: 92px;

  ${lessThan('md')} {
    margin-top: 70px;
  }
`;

const SCRegistrationTitle = styled.h2`
  margin-bottom: 0;
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 36px;
  font-family: var(--landing-main-font);
  text-align: center;
  margin-top: 56px;
  max-width: 715px;
`;

const SCVideoMeta = styled.span`
  font-family: var(--main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 24px;

  text-align: center;
  color: var(--gray-color);
`;

const SCVideoTime = styled.span`
  font-family: var(--main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;

  text-align: center;
  color: #FFFFFF;
`;

const SCPlayerHeader = styled.h5`
  margin-top: 102px;
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 36px;
  margin-bottom: 108px;

  text-align: center;
  color: #FFFFFF;

  ${lessThan('md')} {
    margin-top: 60px;
    margin-bottom: 50px;
  }


  ${lessThan('sm')}{
    margin-top: 13%;
    margin-bottom: 30px;

    font-size: 16px;
    line-height: 20px;
  }
`;

const SCVideoInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SCYTPlayerContainer = styled.div`
  display: none;
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%;
  
  #${PLAYER_ID}{
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const SCPlayerContainer = styled.div`
  width: 100%;
  height: 0;
  padding-bottom: 56.25%;
  position: relative;

  ${lessThan('sm')} {
    padding-bottom: 70%;
  }

  ${lessThan('xs')} {
    padding-bottom: 90%;
  }
  
  &::before {
    pointer-events: none;
    content: "";
    width: 100%;
    height: 0;
    padding-bottom: 56.25%;
    background-size: cover;
    background-image: url('/image/video-background.png');
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0.17;
  }
  
  &[data-is-show-player="true"] {
    ${SCYTPlayerContainer} {
      display: block;
    }

    ${SCVideoInfoContainer} {
      display: none;
    }
  }
`;

const SCPlayButton = styled.button`
  width: 147px;
  height: 147px;
  background-image: url('/icon/landing/community/play-button.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: transparent;
  outline: unset;
  border: unset;
  cursor: pointer;
  margin-bottom: 27px;

  ${lessThan('sm')} {
    margin-bottom: 16px;

    width: 60px;
    height: 60px;
  }
`;

const SCHowUse = styled.div`
  margin-top: 120px;
  background: #3d4144;

  ${lessThan('md')} {
    margin-top: 90px;
  }
`;

const SCShareAndCheck = styled.div`
  margin-top: 83px;
  width: 100%;
  position: relative;
  padding-bottom: 230px;

  ${lessThan('md')} {
    margin-top: 60px;
    padding-bottom: 130px;
  }
`;

const SCShareAndCheckContent = styled(SCContent)`
  flex-direction: row;
  padding-right: 16px;
  padding-left: 16px;

  ${lessThan('sm')} {
    flex-direction: column;
  }
`;

const SCCheck = styled.div`
  width: 50%;
  border-radius: 40px 0 0 0;
  padding: 54px 135px 46px 78px;
  height: 325px;
  background: url('/image/anti-pirate/check.jpg') no-repeat center center / cover;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${lessThan('xl')} {
    padding: 40px 110px 40px 68px;
  }
  
  ${lessThan('md')} {
    padding: 40px 60px 40px;
    height: 370px;
  }

  ${lessThan('sm')} {
    width: 100%;
    height: auto;
    padding: 45px 30px;
    background: url('/image/anti-pirate/check.jpg') no-repeat top center / cover;
  }

  ${lessThan('xs')} {
    padding: 30px 20px;
  }
`;

const SCCheckText = styled.h6`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 28px;
  margin-bottom: 0;
  color: #fff;

  ${lessThan('xs')} {
    font-size: 18px;
    line-height: 24px;
  }
`;

const SCShare = styled.div`
  width: 50%;
  background: var(--primary-hover-color);
  border-radius: 0 0 40px 0;
  padding: 54px 90px 46px 94px;
  height: 325px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${lessThan('xl')} {
    padding: 40px 80px 40px 68px;
  }
  
  ${lessThan('md')} {
    padding: 40px 60px 40px;
    height: 370px;
  }

  ${lessThan('sm')} {
    width: 100%;
    height: auto;
    padding: 30px;
  }

  ${lessThan('xs')} {
    padding: 20px;
  }
`;

const SCCheckButton = styled(SCButton)`
  margin-top: 10px;
  border: 2px solid var(--primary-color);

  &:hover {
    color: #FFFFFF;
  }

  ${lessThan('sm')} {
    margin-top: 30px;
    padding: 10px 16px;
  }

  ${lessThan('xs')} {
    padding: 8px 12px;
  }
`;

const SCShareButton = styled(SCButton)`
  margin-top: 10px;
  background: none;
  border: 2px solid #FFFFFF;

  &:hover {
    color: #FFFFFF;
  }

  ${lessThan('sm')} {
    margin-top: 30px;
    padding: 10px 16px;
  }

  ${lessThan('xs')} {
    padding: 8px 12px;
  }
`;

const SCCurve = styled.div`
  position: absolute;
  bottom: 0;
  left: -2px;
  width: 100%;
  height: 119px;
  background-image: url('/image/curve.svg');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center top;

  ${lessThan('sm')} {
    height: 60px;
  }
`;
